import { Injectable } from "@angular/core";
import { ToastMessageOptions, MessageService as PrimeNgMessageService } from "primeng/api";

export const countSymbol = Symbol('count');

/**
 * Custom `MessageService` that serves to group duplicate sticky messages. Non-sticky messages
 * are added normally.
 */
@Injectable({
    providedIn: 'root'
})
export class MessageService extends PrimeNgMessageService {

    private messages: Map<string, ToastMessageOptions> = new Map();

    private getMessageKey(message: ToastMessageOptions): string {
        return message.key ?? `${message.severity}-${message.summary}-${message.detail}`;
    }

    private addOrUpdate(message: ToastMessageOptions): void {
        console.log(message);
        const messageKey = this.getMessageKey(message);

        if(this.messages.has(messageKey)) {
            const existingMessage = this.messages.get(messageKey);

            super.clear(existingMessage.key);

            existingMessage.data[countSymbol] += 1;

            super.add(existingMessage);

            return;
        }

        const count = message.data?.[countSymbol];

        if(message.data === null || message.data === undefined) {
            message.data = {};
        }

        // NOTE(nicholas-ramsey): We need to be sure users don't set `data` to a value we can't
        // make use off, as it may be `any`.
        const dataIsSafelyMutable = typeof message.data === 'object' && !Array.isArray(message.data);

        if(dataIsSafelyMutable && (typeof count !== 'number' || isNaN(count))) {
            message.data[countSymbol] = 1;
            this.messages.set(messageKey, message);
        }

        super.add(message);
    }

    /**
     * Increments the `data._count` property on duplicate `sticky` messages; otherwise, adds it normally.
     */
    override add(message: ToastMessageOptions): void {
        const shouldGroup = message.sticky !== false;

        if(!shouldGroup)
            super.add(message);
        else
            this.addOrUpdate(message);
    }

    override clear(key?: string): void {
        super.clear(key);

        if (key)
            this.messages.delete(key);
        else
            this.messages.clear();
    }

    removeMessage(message: ToastMessageOptions): void {
        this.messages.delete(this.getMessageKey(message));
    }
}
